import { mediaMax } from "components/designV2/breakpoints";
import { Spacing } from "components/designV2/constants";
import { ColorV2 } from "config/colors";
import { toRgba } from "config/colors.rgba.logic";
import { TypographyV2 } from "config/font";
import styled, { css, keyframes } from "styled-components";

export const TipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

export const PeriodBar = styled.div`
  align-items: center;
  background-color: ${ColorV2.White};
  border-radius: 24px;
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: space-between;
  min-height: 104px;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  width: 100%;

  ${mediaMax.tablet} {
    align-items: flex-start;
    flex-direction: column;
  }

  ${mediaMax.mobile} {
    min-height: 88px;
    padding: ${Spacing.sm}px;
  }
`;

export const EmptyState = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: clamp(360px, 48vh, 640px);
  padding: ${Spacing.lg}px;
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

export const LoadingSpinner = styled.span`
  animation: ${spin} 0.8s linear infinite;
  border: 3px solid ${ColorV2.Blue6};
  border-radius: 50%;
  border-top-color: ${ColorV2.Blue1};
  display: inline-flex;
  height: 40px;
  width: 40px;
`;

export const EmptyContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  max-width: 480px;
  text-align: center;
`;

export const EmptyTitle = styled.h3`
  ${TypographyV2.h5}
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const EmptyDescription = styled.p`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue1};
  margin: 0 0 ${Spacing.sm}px;
`;

export const DatePickerSlot = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${Spacing.sm}px;
`;

export const DuplicateDatesModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

export const DuplicateDatesLead = styled.div`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;

export const DuplicateDatesList = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue2};
  line-height: 1.5;
  word-break: break-word;
`;

export const DuplicateDatesDescription = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  line-height: 1.5;
`;

export const DuplicateDatesModalActions = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: flex-end;

  ${mediaMax.mobile} {
    align-items: stretch;
    flex-direction: column-reverse;
  }
`;

export const TipsWarningModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

export const TipsWarningModalMessage = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue2};
  line-height: 1.5;
`;

export const TipsWarningModalActions = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: flex-end;

  ${mediaMax.mobile} {
    align-items: stretch;
    flex-direction: column-reverse;
  }
`;

export const TipsTabActions = styled.div`
  display: flex;
  justify-content: flex-end;

  ${mediaMax.mobile} {
    justify-content: flex-start;
  }
`;

export const TipsTabBar = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: space-between;

  ${mediaMax.mobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const SummaryCards = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

export const SummaryCard = styled.button<{ $isPrimary?: boolean }>`
  align-items: center;
  appearance: none;
  background-color: ${(props) =>
    props.$isPrimary ? ColorV2.Blue1 : ColorV2.White};
  border: 1.5px solid
    ${(props) => (props.$isPrimary ? ColorV2.Blue1 : ColorV2.Blue6)};
  border-radius: 8px;
  color: ${(props) => (props.$isPrimary ? ColorV2.White : ColorV2.Blue1)};
  cursor: pointer;
  display: flex;
  font: inherit;
  gap: ${Spacing.sm}px;
  min-width: 184px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  text-align: left;
`;

export const SummaryIcon = styled.span<{ $isPrimary?: boolean }>`
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

export const SummaryValue = styled.div<{ $isPrimary?: boolean }>`
  ${TypographyV2.h5}
  color: ${(props) => (props.$isPrimary ? ColorV2.White : ColorV2.Blue1)};
`;

export const SummaryLabel = styled.div<{ $isPrimary?: boolean }>`
  ${TypographyV2.s1}
  color: ${(props) => (props.$isPrimary ? ColorV2.White : ColorV2.Blue4)};
`;

export const TipRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  padding-bottom: ${Spacing.xl}px;
`;

export const TipBulkBar = styled.div`
  bottom: 24px;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 0 ${Spacing.lg}px;
  pointer-events: none;
  position: fixed;
  right: 0;
  z-index: 40;
`;

export const TipBulkBarCard = styled.div`
  align-items: center;
  background: ${ColorV2.White};
  border: 1px solid ${ColorV2.Blue6};
  border-radius: 44px;
  box-shadow: 0 18px 40px rgba(12, 45, 72, 0.16);
  display: flex;
  gap: ${Spacing.lg}px;
  justify-content: space-between;
  max-width: 760px;
  min-height: 88px;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  pointer-events: auto;
  width: min(100%, 760px);

  ${mediaMax.tablet} {
    align-items: flex-start;
    border-radius: 24px;
    flex-direction: column;
    gap: ${Spacing.md}px;
  }
`;

export const TipBulkInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const TipBulkTitle = styled.span`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;

export const TipBulkTotal = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const TipBulkActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm}px;
  justify-content: flex-end;

  ${mediaMax.tablet} {
    justify-content: flex-start;
  }
`;

export const TipRowCard = styled.div`
  background-color: ${ColorV2.White};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const TipRow = styled.div`
  align-items: center;
  display: grid;
  column-gap: ${Spacing.lg}px;
  row-gap: ${Spacing.md}px;
  grid-template-columns:
    24px minmax(260px, 300px) minmax(224px, 240px) minmax(240px, 260px)
    minmax(0, 1fr) 32px 32px;
  min-height: 72px;
  padding: ${Spacing.sm}px ${Spacing.md}px;

  ${mediaMax.tablet} {
    column-gap: ${Spacing.md}px;
    grid-template-columns: 24px minmax(0, 1fr) 32px 32px;
  }
`;

export const RowActionSlot = styled.div<{ $column: 6 | 7 }>`
  align-items: center;
  display: flex;
  grid-column: ${(props) => props.$column};
  justify-content: center;

  ${mediaMax.tablet} {
    grid-column: ${(props) => (props.$column === 6 ? 3 : 4)};
    grid-row: 1;
  }
`;

export const TipWorkersPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  padding: ${Spacing.lg}px ${Spacing.md}px ${Spacing.md}px 48px;

  ${mediaMax.tablet} {
    padding: ${Spacing.md}px;
  }
`;

export const TipWorkerHeader = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns:
    minmax(180px, 1.2fr) minmax(120px, 0.8fr) minmax(96px, 0.6fr)
    minmax(96px, 0.6fr) minmax(120px, 0.8fr);

  ${mediaMax.tablet} {
    display: none;
  }
`;

export const TipWorkerRow = styled.div`
  ${TypographyV2.b2}
  align-items: center;
  color: ${ColorV2.Blue1};
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns:
    minmax(180px, 1.2fr) minmax(120px, 0.8fr) minmax(96px, 0.6fr)
    minmax(96px, 0.6fr) minmax(120px, 0.8fr);
  min-height: 40px;

  ${mediaMax.tablet} {
    align-items: flex-start;
    border-top: 1px solid ${ColorV2.Blue6};
    display: flex;
    flex-direction: column;
    gap: ${Spacing.xs}px;
    padding-top: ${Spacing.sm}px;
  }
`;

export const TipWorkerNameCell = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.sm}px;
  min-width: 0;
`;

export const TipWorkerAvatar = styled.div`
  ${TypographyV2.s1}
  align-items: center;
  background: ${ColorV2.Blue6};
  border-radius: 999px;
  color: ${ColorV2.Blue1};
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;
`;

export const TipWorkerNameText = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TipWorkerCell = styled.div`
  min-width: 0;
`;

export const TipWorkerPaymentText = styled.div`
  min-width: 0;
`;

export const TipWorkerMissingText = styled.span`
  color: ${ColorV2.NegativeRed};
`;

export const TipNoWorkers = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

export const TipDistributeAction = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  grid-column: 5;
  justify-content: flex-end;
  min-width: 0;

  ${mediaMax.tablet} {
    grid-column: 2 / -1;
    grid-row: 4;
    justify-content: flex-start;
  }
`;

export const TipPaymentStatusPill = styled.span<{
  $tone: "danger" | "success" | "warning";
}>`
  ${TypographyV2.s1}
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.xs}px ${Spacing.md}px;
  white-space: nowrap;

  ${(props) =>
    props.$tone === "success" &&
    css`
      background: ${toRgba(ColorV2.PositiveGreen, 0.14)};
      color: ${ColorV2.PositiveGreen};
    `}

  ${(props) =>
    props.$tone === "warning" &&
    css`
      background: ${toRgba(ColorV2.DarkAmbigiousYellow, 0.16)};
      color: ${ColorV2.DarkAmbigiousYellow};
    `}

  ${(props) =>
    props.$tone === "danger" &&
    css`
      background: ${toRgba(ColorV2.NegativeRed, 0.14)};
      color: ${ColorV2.NegativeRed};
    `}
`;

export const RowCheckbox = styled.input`
  accent-color: ${ColorV2.Blue1};
  height: 24px;
  margin: 0;
  width: 24px;
`;

export const RowTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const RowDate = styled.div`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;

export const RowMeta = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const TipRowValue = styled.label`
  ${TypographyV2.b2}
  align-items: center;
  color: ${ColorV2.Blue4};
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: flex-start;
  min-width: 0;
  white-space: nowrap;

  strong {
    ${TypographyV2.b1}
    color: ${ColorV2.Blue1};
  }

  ${mediaMax.tablet} {
    grid-column: 2 / -1;
    justify-content: flex-start;
  }
`;

export const TipAmountInput = styled.input`
  ${TypographyV2.b2}
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  border-radius: 16px;
  color: ${ColorV2.Blue1};
  height: 44px;
  padding: 0 ${Spacing.sm}px;
  width: 112px;

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  &:disabled {
    background-color: ${ColorV2.Blue7};
    border-color: ${ColorV2.Blue6};
    color: ${ColorV2.Blue4};
    cursor: not-allowed;
  }
`;

export const RowActionButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  width: 32px;

  &:hover {
    cursor: pointer;
  }
`;

export const ErrorText = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.NegativeRed};
  text-align: center;
`;
